import { destr } from 'destr'

const businessRepository = (fetch: CustomFetch) => ({
  async get(businessId: string) {
    return fetch<BusinessData>(`/v1/businesses/${businessId}`)
  },
  async getDefault() {
    return fetch<BusinessData>('/v2/businesses/user', { parseResponse: destr })
  },
  async create(registerData: RegisterBusinessPayload) {
    return fetch<Partial<BusinessData>>('/v2/businesses', {
      method: 'POST',
      body: objectToSnakeCase(registerData),
      parseResponse: destr,
    })
  },
  async convertFreeToStarter(registerData: ConvertFreeToStarter) {
    return fetch<Partial<BusinessData>>('/v2/businesses/convert/free', {
      method: 'POST',
      body: objectToSnakeCase(registerData),
      parseResponse: destr,
    })
  },
  async cancelTrial(businessId: string) {
    await fetch(`/v1/businesses/${businessId}/cancel-trial`, { method: 'POST' })
  },
  async getBillingAddress(businessId: string) {
    return fetch<Partial<BusinessBillingInfo>>(`/v2/businesses/${businessId}/billing`)
  },
  async getExtras(businessId: string) {
    return fetch<BusinessExtras[]>(`/v2/businesses/${businessId}/customextras`, { parseResponse: destr })
  },
  async getPublicInfo(businessId: string) {
    return fetch<PublicBusinessInfo>(`/v2/businesses/${businessId}/public-information`)
  },
  async getAdmins(businessId: string) {
    const { admins } = await fetch<{ admins: Admin[] }>(`/v1/businesses/${businessId}/admins`)
    return admins
  },
  async makeAdmins(businessId: string, memberIds: string[]) {
    return fetch(`/v1/businesses/${businessId}/change-to-admins`, {
      method: 'POST',
      body: { user_ids: memberIds },
    })
  },
  async getApiUsers(businessId: string) {
    const { apiUsers } = await fetch<{ apiUsers: APIKeyList }>(`/v1/businesses/${businessId}/apis`)
    return apiUsers
  },
  async addDomain(businessId: string, domain: string): Promise<DomainEntry[]> {
    const response = await fetch<{ business: BusinessData }>(`/v1/businesses/${businessId}/domains`, {
      method: 'POST',
      body: { domain },
    })

    return response.business.domains
  },
  async removeDomain(businessId: string, domain: string): Promise<DomainEntry[]> {
    const response = await fetch<{ business: BusinessData }>(
      `/v1/businesses/${businessId}/domains/${encodeURIComponent(domain)}`,
      { method: 'DELETE' }
    )

    return response.business.domains
  },
  async createChargebeeSessionPortal(businessId: string) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    return fetch<Function>(`/v2/businesses/${businessId}/chargebee/portal_session`, {
      method: 'POST',
    })
  },
  async updateSupportContact(
    businessId: string,
    contactInfo: { email: string; name: string; phone: string; settings: { supportContact: SupportContactData } }
  ) {
    // FIXME: The response of this request has a nested `business` property, which is inconsistent when compared to similar requests
    const { business } = await fetch<{ business: BusinessData }>(`/v1/businesses/${businessId}`, {
      method: 'PUT',
      body: objectToSnakeCase(contactInfo),
    })

    return business
  },
  async acceptGtc(businessId: string) {
    return await fetch<BusinessData>(`/v2/businesses/${businessId}/base`, {
      method: 'PUT',
      body: objectToSnakeCase({ gtcAccepted: true }),
    })
  },
  async getSubscriptionPrices(businessId: string) {
    return fetch<PricePlanPrices>(`/v2/businesses/${businessId}/subscriptions/prices`, {
      parseResponse: destr,
    })
  },
  async registerEmailDomain(businessId: string, payload: RegisterEmailDomainPayload) {
    return fetch<BusinessEmailDomain>(`/v2/businesses/${businessId}/settings/email_domain`, {
      method: 'POST',
      body: objectToSnakeCase(payload),
      parseResponse: destr,
    })
  },
  async getEmailDomain(businessId: string): Promise<BusinessEmailDomain | null> {
    try {
      return await fetch<BusinessEmailDomain>(`/v2/businesses/${businessId}/settings/email_domain`, {
        parseResponse: destr,
      })
    } catch (error) {
      if (isFetchErrorWithStatus(error, 404)) return null
      throw error
    }
  },
  async getDefaultSender(businessId: string): Promise<string> {
    const { defaultSender } = await fetch<{ defaultSender: string }>(
      `/v2/businesses/${businessId}/settings/email_domain/default_sender`,
      { parseResponse: destr }
    )
    return defaultSender
  },
  async updateEmailDomain(businessId: string, updates: UpdateEmailDomainPayload) {
    return fetch<BusinessEmailDomain>(`/v2/businesses/${businessId}/settings/email_domain`, {
      method: 'PATCH',
      body: objectToSnakeCase(updates),
      parseResponse: destr,
    })
  },
  async verifyEmailDomain(businessId: string) {
    return fetch<BusinessEmailDomain>(`/v2/businesses/${businessId}/settings/email_domain/verify`, {
      method: 'POST',
      parseResponse: destr,
    })
  },
  async removeEmailDomain(businessId: string) {
    await fetch(`/v2/businesses/${businessId}/settings/email_domain`, { method: 'DELETE' })
  },
})

export default businessRepository
